import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { ChevronLeft, Edit05 } from "@untitledui/icons";
import { useState } from "react";
import { Focusable } from "react-aria-components";
import { useTargetCollaborationGroupDetail } from "@/api/collaborations";
import { useSmartCollabGroupAutomations } from "@/api/smart-collab-groups";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { RulesRunningLink } from "@/components/rules/rules-running-link";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useMerchant } from "@/providers/MerchantProvider";
import type { targetCollaborationGroupDetailRoute } from "@/router";
import { formatDate } from "@/utils/date-conversion";
import { LAYOUT_CONSTRAINTS } from "@/utils/layout-constants";
import { CollaborationCreatorsTable } from "./collaboration-creators-table";
import { AutomationsItem, Dot, MetadataItem } from "./collaboration-detail-header";
import type { CreatorStatusTab } from "./collaboration-table-columns";
import { CollaborationInsightsPanel } from "./insights";
import { ProductChips } from "./product-chips";
import { RecommendedCreatorsButton } from "./recommended-creators-button";
import { ReviewSampleRequestsButton } from "./review-sample-requests-button";
import { SmartCollabGroupBadge } from "./smart-collab-group-badge";
import { SMART_GROUP_EDIT_DISABLED_REASON } from "./smart-group-edit";
import { SmartUnboxGroupBadge } from "./smart-unbox-group-badge";
import { ViewAutomationTriggersModal } from "./view-automation-triggers-modal";

export const TargetCollaborationGroupDetail = () => {
  const { groupId } = useParams({ strict: false }) as { groupId: string };
  const navigate = useNavigate() as ReturnType<(typeof targetCollaborationGroupDetailRoute)["useNavigate"]>;
  const { tab: activeTab, source } = useSearch({ strict: false }) as ReturnType<
    (typeof targetCollaborationGroupDetailRoute)["useSearch"]
  >;
  const isSmartCollabGroup = source === "smart-collab-group";
  const merchant = useMerchant();
  const newCollabsGroupsEnabled = useFeatureFlag("F-new-collabs-groups");
  const goBackToCollaborations = () => {
    navigate({
      to: newCollabsGroupsEnabled
        ? "/merchant/$merchantId/collabs/groups/target"
        : "/merchant/$merchantId/collabs/target",
      params: { merchantId: merchant },
    });
  };
  const [breakdownFilter, setBreakdownFilter] = useState<string | null>(null);
  const [automationsOpen, setAutomationsOpen] = useState(false);

  const { data: group, isLoading, error } = useTargetCollaborationGroupDetail(groupId);
  const groupAutomations = useSmartCollabGroupAutomations(groupId, { enabled: isSmartCollabGroup });

  const statusCounts = group?.statusCounts ?? {
    all: 0,
    invited: 0,
    sampleRequested: 0,
    sampleStatus: 0,
    pendingPost: 0,
    postedVideo: 0,
    postedNoGmv: 0,
    cancelled: 0,
  };

  const tabs = [
    { id: "all" as const, label: "All", badge: statusCounts.all },
    { id: "invited" as const, label: "Invited", badge: statusCounts.invited },
    ...(group?.hasFreeSample && !group?.isSampleApprovalExempt
      ? [
          {
            id: "sample_requested" as const,
            label: "Sample Requested",
            badge: statusCounts.sampleRequested,
          },
        ]
      : []),
    {
      id: "sample_status" as const,
      label: "Sample Status",
      badge: statusCounts.sampleStatus,
    },
    {
      id: "pending_post" as const,
      label: "Pending Post",
      badge: statusCounts.pendingPost,
    },
    {
      id: "posted_no_gmv" as const,
      label: "Posted No GMV",
      badge: statusCounts.postedNoGmv,
    },
    {
      id: "posted_video" as const,
      label: "Generated GMV",
      badge: statusCounts.postedVideo,
    },
    {
      id: "cancelled" as const,
      label: "Cancelled",
      badge: statusCounts.cancelled,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-unbox-cream-25">
        <LoadingIndicator label="Loading collaboration group..." size="lg" />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-unbox-cream-25">
        <EmptyState size="md">
          <div className="pb-6">
            <EmptyState.FeaturedIcon color="gray" />
          </div>
          <EmptyState.Content>
            <EmptyState.Title>Group not found</EmptyState.Title>
            <EmptyState.Description>
              The collaboration group you're looking for doesn't exist or you don't have access to it.
            </EmptyState.Description>
          </EmptyState.Content>
          <EmptyState.Footer>
            <Button color="secondary" size="md" onClick={goBackToCollaborations}>
              <div className="flex items-center">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Collaborations
              </div>
            </Button>
          </EmptyState.Footer>
        </EmptyState>
      </div>
    );
  }

  const setActiveTab = (next: CreatorStatusTab) => {
    navigate({ search: (prev) => ({ ...prev, tab: next as typeof activeTab }) });
  };

  const handleTabChange = (key: string | number) => {
    navigate({ search: (prev) => ({ ...prev, tab: key as CreatorStatusTab }) });
  };

  const typeLabel = group.type === "STANDARD" ? "Target" : "Top Creator Program";

  return (
    <div className="flex flex-col w-full min-h-screen items-center bg-unbox-cream-25">
      <div className={`${LAYOUT_CONSTRAINTS.CONTAINER_WIDTH} flex flex-col px-16 pt-8 pb-8`}>
        {/* Back button */}
        <Button
          color="tertiary"
          size="sm"
          onClick={goBackToCollaborations}
          className="text-quaternary hover:text-primary pl-0 w-fit mb-3"
        >
          <div className="flex items-center">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Collaborations
          </div>
        </Button>

        {/* Header with group info */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-row justify-between items-start">
            <div className="flex flex-col gap-1">
              <Tooltip title={group.name}>
                <Focusable>
                  <h1 className="inline-block min-w-0 max-w-[80ch] truncate text-left text-xl text-primary outline-hidden">
                    {group.name}
                  </h1>
                </Focusable>
              </Tooltip>
              {isSmartCollabGroup ? <SmartCollabGroupBadge /> : <SmartUnboxGroupBadge />}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {group.collaborationIds[0] && (
                <RecommendedCreatorsButton collaborationId={group.collaborationIds[0]} groupId={groupId} />
              )}
              <ReviewSampleRequestsButton
                collaborationId={group.collaborationIds[0]}
                sampleRequestedCount={statusCounts.sampleRequested}
              />
              <Tooltip title={SMART_GROUP_EDIT_DISABLED_REASON}>
                <TooltipTrigger>
                  <span>
                    <Button color="secondary" size="sm" iconLeading={<Edit05 className="w-4 h-4" />} isDisabled>
                      Edit
                    </Button>
                  </span>
                </TooltipTrigger>
              </Tooltip>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-12">
            <ProductChips
              products={group.products.map((product) => ({
                title: product.productTitle ?? product.productId,
                commissionRate: product.commissionRate,
                imageUrl: null,
              }))}
              visible={1}
            />
            <Dot />
            <MetadataItem label="Type" value={typeLabel} />
            <Dot />
            <MetadataItem label="Expires" value={formatDate(group.endTime)} />
            <Dot />
            <MetadataItem label="Free Sample" value={group.hasFreeSample ? "On" : "Off"} />
            <Dot />
            <RulesRunningLink ruleCount={group.matchingRules.length} />
            {isSmartCollabGroup && (
              <AutomationsItem
                automations={groupAutomations.data ?? []}
                isLoading={groupAutomations.isLoading}
                isError={groupAutomations.isError}
                onOpen={() => setAutomationsOpen(true)}
              />
            )}
          </div>
        </div>

        {isSmartCollabGroup && (
          <ViewAutomationTriggersModal
            open={automationsOpen}
            onOpenChange={setAutomationsOpen}
            scope={{ kind: "group", groupId }}
            editDisabledReason={SMART_GROUP_EDIT_DISABLED_REASON}
          />
        )}

        {/* Collaboration Insights Panel — between header and status tabs */}
        <CollaborationInsightsPanel
          groupId={groupId}
          showRecommendations
          onFilterChange={setActiveTab}
          onBreakdownFilterChange={setBreakdownFilter}
        />

        {/* Status filter tabs */}
        <Tabs selectedKey={activeTab} onSelectionChange={handleTabChange} className="mb-6">
          <Tabs.List type="underline" items={tabs}>
            {(tab) => <Tabs.Item {...tab} />}
          </Tabs.List>
        </Tabs>

        {/* Creators table */}
        <CollaborationCreatorsTable
          collaborationId={group.collaborationIds[0]}
          collaborationName={group.name}
          statusFilter={activeTab}
          groupId={groupId}
          breakdownFilter={breakdownFilter}
          onClearBreakdownFilter={() => setBreakdownFilter(null)}
        />
      </div>
    </div>
  );
};
